import React, { useMemo } from 'react';
import { Tabs } from '@ygtechnologies/rubus-ui';
import { useParams } from 'react-router';
import { Link, useLocation } from 'react-router-dom';
import { useAsyncState, useAsyncStateWithIsLoading } from '@/utils/hooks';
import { getEmailContents, getEmailOverrideScopes, resolveEmailLayout } from '@/services/emailTemplates';
import ScopeBar from './ScopeBar';
import EmailContentsTable from './EmailContentsTable';
import LayoutsView from './LayoutsView';
import { useScopeInUrl, useScopeNamesFromResolution } from './shared';

export type EmailTemplatesTab = 'contents' | 'layouts';

const tabs: { id: EmailTemplatesTab; name: string }[] = [
    { id: 'contents', name: 'Contents' },
    { id: 'layouts', name: 'Layouts' },
];

/**
 * The email templates section, scope-first: the profile / project / language chosen in the bar lives in
 * the query string and follows the user across the tabs and into the per-template editors. Editing is
 * usually about one project or seller profile, not one template.
 */
const EmailTemplatesPage: React.FC = () => {
    const { tab } = useParams<{ tab?: EmailTemplatesTab }>();
    const location = useLocation();
    const [scope, setScope] = useScopeInUrl();
    const activeTab: EmailTemplatesTab = tab || 'contents';

    // Fetched here rather than in the table so the bar can offer the platform's languages on every tab.
    const [contents, , , isLoadingContents] = useAsyncStateWithIsLoading(
        () => getEmailContents({ projectId: scope.projectId, profileId: scope.profileId }),
        [scope.projectId, scope.profileId],
        null
    );
    const [overrideScopes] = useAsyncState(() => getEmailOverrideScopes(), [], null);

    // Deep links carry only ids. The layout resolution endpoint names them (and supplies a project's
    // owning profile), so the bar can label the pickers on any tab; skipped once the names are known.
    const needsNames =
        (scope.projectId != null && !scope.projectName) || (scope.profileId != null && !scope.profileName);
    const [nameResolution] = useAsyncState(
        async () =>
            needsNames
                ? resolveEmailLayout({
                      projectId: scope.projectId,
                      profileId: scope.profileId,
                      languageCode: null,
                  })
                : null,
        [scope.projectId, scope.profileId, needsNames],
        null
    );
    useScopeNamesFromResolution(scope, setScope, nameResolution);

    const languages = useMemo(
        () =>
            (contents ?? [])
                .flatMap((x) => x.languages)
                .filter((x, index, all) => all.indexOf(x) === index)
                .sort((a, b) => a.localeCompare(b)),
        [contents]
    );
    const overriddenProjectIds = useMemo(() => new Set(overrideScopes?.projectIds ?? []), [overrideScopes]);
    const overriddenProfileIds = useMemo(() => new Set(overrideScopes?.profileIds ?? []), [overrideScopes]);

    return (
        <div>
            <h2>Email templates</h2>

            <ScopeBar
                scope={scope}
                onChange={setScope}
                languages={languages}
                overriddenProjectIds={overriddenProjectIds}
                overriddenProfileIds={overriddenProfileIds}
            />

            <Tabs.Container activeId={activeTab} variant="underline">
                {tabs.map((tabConfig) => (
                    <Tabs.Tab
                        key={tabConfig.id}
                        id={tabConfig.id}
                        as={Link}
                        // Carry the scope across tab switches.
                        to={`/email-templates/${tabConfig.id}${location.search}`}
                        label={tabConfig.name}
                    />
                ))}
                <Tabs.Content>
                    <div className="tab-panel">
                        {activeTab === 'contents' ? (
                            <EmailContentsTable data={contents} isLoading={isLoadingContents} />
                        ) : (
                            <LayoutsView />
                        )}
                    </div>
                </Tabs.Content>
            </Tabs.Container>
        </div>
    );
};

export default EmailTemplatesPage;
